import type { StorageManager } from '@/storage/save-data';
import type { LevelData } from '@/game/level';
import type { SyncManager } from '@/sync/sync-manager';
import type { LevelSyncStatus } from '@/sync/sync-types';
import { CompletionListModal } from '@/ui/completion-list-modal';

export interface LevelInfo {
  id: string;
  name: string;
  difficulty: string;
  bpm: number;
}

export type LevelSelectTab = 'builtin' | 'user' | 'downloaded';

export class LevelSelectScreen {
  private element: HTMLElement | null = null;
  private tab: LevelSelectTab = 'builtin';
  private syncManager: SyncManager | null = null;
  private storage: StorageManager | null = null;
  private syncMap: Map<string, LevelSyncStatus> = new Map();
  private remoteOnly: Array<{
    remoteId: string; name: string; difficulty: string; isPublic: boolean; updatedAt: number;
  }> = [];
  private completionsModal = new CompletionListModal();

  onSelect: ((levelId: string, kind: 'builtin' | 'user') => void) | null = null;
  onBack: (() => void) | null = null;
  onUnlockAll: (() => void) | null = null;
  onEdit: ((levelId: string) => void) | null = null;
  onClone: ((levelId: string, kind: 'builtin' | 'user') => void) | null = null;
  onDelete: ((levelId: string) => void) | null = null;
  onNew: (() => void) | null = null;
  onImport: (() => void) | null = null;
  onSyncAction: ((action: 'push' | 'pull' | 'publish' | 'unpublish' | 'resolve' | 'delete-remote' | 'clear-link',
                  levelId: string) => void) | null = null;
  onPullRemoteOnly: ((remoteId: string) => void) | null = null;
  onDownloadedAction: ((action: 'play' | 'remix' | 'unlink' | 'pull', remoteId: string) => void) | null = null;
  onBrowse: (() => void) | null = null;

  setSync(manager: SyncManager, storage: StorageManager): void {
    this.syncManager = manager;
    this.storage = storage;
  }

  show(builtins: LevelInfo[], userLevels: LevelData[], storage: StorageManager): void {
    this.hide();
    const el = document.createElement('div');
    el.id = 'level-select';
    el.style.cssText = `
      position: fixed; inset: 0;
      background: rgba(10, 10, 26, 0.95);
      display: flex; flex-direction: column;
      align-items: center; padding: 2vh 16px;
      z-index: 60; backdrop-filter: blur(8px);
      font-family: 'Courier New', monospace;
    `;

    const tabs: Array<{ key: LevelSelectTab; label: string; hidden?: boolean }> = [
      { key: 'builtin', label: 'Built-in' },
      { key: 'user', label: 'My Levels' },
      { key: 'downloaded', label: `Downloaded (${storage.getDownloadedLevels().length})`, hidden: !storage.getSession() },
    ];
    const tabsHtml = tabs.filter((t) => !t.hidden).map((t) =>
      `<button class="ls-tab ${this.tab === t.key ? 'active' : ''}" data-tab="${t.key}">${escape(t.label)}</button>`,
    ).join('');

    el.innerHTML = `
      <div style="color:#00ffff;font-size:clamp(1.2rem,4vw,1.6rem);font-weight:bold;
                  text-shadow:0 0 20px #00ffff;letter-spacing:0.15em;margin-bottom:0.4rem">
        SELECT LEVEL
      </div>
      <div class="ls-tabs" style="display:flex;gap:4px;margin-bottom:0.8rem">
        ${tabsHtml}
      </div>
      <div class="ls-list" style="flex:1;overflow:auto;width:100%;max-width:440px"></div>
      <div style="display:flex;gap:10px;margin-top:1rem;flex-wrap:wrap;justify-content:center">
        <button id="ls-back" class="menu-btn menu-btn-exit">BACK</button>
        <button id="ls-unlock" class="menu-btn" style="font-size:0.7rem">UNLOCK ALL</button>
        <button id="ls-import" class="menu-btn" style="font-size:0.7rem">⬇ IMPORT</button>
      </div>
      <style>
        .ls-tab { background:#111; color:#aaa; border:1px solid #333; padding:8px 14px; font:inherit; cursor:pointer; min-height:36px; }
        .ls-tab.active { background:#00ffff22; color:#00ffff; border-color:#00ffff; }
        .ls-card { background:rgba(0,255,255,0.03); border:1px solid #00ffff44; border-radius:6px;
                   padding:10px 14px; margin:6px 0; width:100%; box-sizing:border-box; }
        .ls-card .row { display:flex; justify-content:space-between; align-items:center; gap:8px; }
        .ls-card .actions button { background:#111; color:#ddd; border:1px solid #333;
                                   padding:6px 8px; font:inherit; margin-left:4px; min-height:32px; font-size:11px; }
        .ls-new { background:#00ffff11; border:1px dashed #00ffff66; text-align:center; color:#00ffff; cursor:pointer; padding:20px; }
      </style>
    `;
    document.body.appendChild(el);
    this.element = el;

    el.querySelectorAll<HTMLButtonElement>('.ls-tab').forEach((btn) => {
      btn.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
        this.tab = btn.dataset.tab as LevelSelectTab;
        this.show(builtins, userLevels, storage);
      });
    });

    const list = el.querySelector('.ls-list') as HTMLElement;
    if (this.tab === 'builtin') {
      this.renderBuiltins(list, builtins, storage);
    } else if (this.tab === 'downloaded') {
      this.renderDownloaded(list, storage.getDownloadedLevels());
      if (storage.getSession()) {
        this.loadDownloadedStatuses(list, storage.getDownloadedLevels());
      }
    } else {
      this.renderUsers(list, userLevels);
      if (this.storage?.getSession()) {
        this.loadSyncStatusesAndRender(list, userLevels);
      }
    }

    el.querySelector('#ls-back')!.addEventListener('pointerdown', (e) => { e.stopPropagation(); this.hide(); this.onBack?.(); });
    el.querySelector('#ls-unlock')!.addEventListener('pointerdown', (e) => { e.stopPropagation(); this.hide(); this.onUnlockAll?.(); });
    el.querySelector('#ls-import')!.addEventListener('pointerdown', (e) => { e.stopPropagation(); this.onImport?.(); });
  }

  private renderBuiltins(list: HTMLElement, builtins: LevelInfo[], storage: StorageManager): void {
    list.innerHTML = '';
    for (const level of builtins) {
      const unlocked = storage.isUnlocked(level.id);
      const record = storage.getRecord(level.id);
      const stars = record.starsCollected.map((s) => s ? '★' : '☆').join('');
      const card = document.createElement('div');
      card.className = 'ls-card';
      card.innerHTML = `
        <div class="row">
          <div>
            <div style="color:${unlocked ? '#00ffff' : '#666'};font-weight:bold">${unlocked ? '' : '🔒 '}${escape(level.name)}</div>
            <div style="font-size:10px;color:#678">${escape(level.difficulty)} · ${level.bpm} BPM</div>
          </div>
          <div style="color:#ffdd33">${unlocked ? stars : ''}</div>
        </div>
        <div class="row actions" style="margin-top:6px;justify-content:flex-end">
          ${unlocked ? '<button data-act="play">▶ Play</button>' : ''}
          <button data-act="clone">📋 Clone</button>
        </div>
      `;
      list.appendChild(card);
      card.querySelector('[data-act="play"]')?.addEventListener('click', (e) => { e.stopPropagation(); this.hide(); this.onSelect?.(level.id, 'builtin'); });
      card.querySelector('[data-act="clone"]')?.addEventListener('click', (e) => { e.stopPropagation(); this.onClone?.(level.id, 'builtin'); });
    }
  }

  private loadSyncStatusesAndRender(list: HTMLElement, userLevels: LevelData[]): void {
    if (!this.syncManager) return;
    const mgr = this.syncManager;
    // Fire both calls in parallel. We render once both complete; either failure
    // degrades gracefully (empty map / empty remote list).
    Promise.all([
      mgr.checkSyncStatuses(userLevels).catch(() => new Map<string, LevelSyncStatus>()),
      mgr.listMineLevels().catch(() => [] as Awaited<ReturnType<typeof mgr.listMineLevels>>),
    ]).then(([statusMap, mine]) => {
      this.syncMap = statusMap;
      const localRemoteIds = new Set(
        userLevels.map((l) => l.meta.remoteId).filter((id): id is string => !!id),
      );
      this.remoteOnly = mine
        .filter((m) => !localRemoteIds.has(m.remoteId))
        .map((m) => ({
          remoteId: m.remoteId, name: m.name, difficulty: m.difficulty,
          isPublic: m.isPublic, updatedAt: m.updatedAt,
        }));
      this.renderUsers(list, userLevels);
    });
  }

  private loadDownloadedStatuses(list: HTMLElement, downloaded: LevelData[]): void {
    if (!this.syncManager) return;
    const mgr = this.syncManager;
    mgr.checkSyncStatuses(downloaded).then(
      (map) => { this.syncMap = map; this.renderDownloaded(list, downloaded); },
      () => { this.syncMap = new Map(); },
    );
  }

  private renderDownloaded(list: HTMLElement, downloaded: LevelData[]): void {
    list.innerHTML = '';
    const browseBtn = document.createElement('button');
    browseBtn.id = 'ls-browse';
    browseBtn.style.cssText = 'background:#00ffff22;border:1px solid #00ffff;color:#00ffff;padding:10px 14px;font:inherit;font-size:0.85rem;border-radius:4px;margin:0 0 10px;cursor:pointer;width:100%;letter-spacing:0.05em';
    browseBtn.innerHTML = '🔍 BROWSE LEVELS';
    browseBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.hide();
      this.onBrowse?.();
    });
    list.appendChild(browseBtn);

    if (downloaded.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'color:#555;text-align:center;padding:24px 12px;font-size:0.85rem;background:#0d0d1d;border:1px dashed #333;border-radius:6px';
      empty.textContent = 'No downloaded levels yet — tap BROWSE LEVELS above to find some.';
      list.appendChild(empty);
      return;
    }
    for (const lvl of downloaded) {
      const card = document.createElement('div');
      card.className = 'ls-card';
      const status = this.syncMap.get(lvl.meta.id);
      const chipHtml = this.renderDownloadedChip(status?.state ?? 'synced');
      const updateBtn = status?.state === 'update_available'
        ? `<button data-dl-act="pull" style="background:#8855ff22;border:1px solid #8855ff;color:#b99aff;padding:4px 10px;font:inherit;font-size:0.7rem;border-radius:3px">↓ PULL</button>`
        : '';
      const tintColor = status?.state === 'update_available' ? '#8855ff88'
        : status?.state === 'removed_from_server' ? '#888' : '#00ffff44';
      card.style.borderColor = tintColor;
      const rid = lvl.meta.remoteId!;
      const cache = this.storage?.getDownloadedLevelCache(rid) ?? null;
      const verifierBadge = this.renderVerifierBadge(cache?.verifier ?? null, cache?.cachedAt ?? null, rid);
      card.innerHTML = `
        <div class="row">
          <div>
            <div style="color:#fff;font-weight:bold">${escape(lvl.meta.name)}</div>
            <div style="font-size:10px;color:#678">${escape(lvl.meta.difficulty)} · by <span style="color:#00ffff">${escape(lvl.meta.author)}</span></div>
            ${verifierBadge}
          </div>
          <div style="display:flex;align-items:center;gap:6px">${chipHtml} ${updateBtn}</div>
        </div>
        <div class="row actions" style="margin-top:6px;justify-content:flex-end">
          <button data-dl-act="play">▶ Play</button>
          <button data-dl-act="remix">✎ Remix</button>
          <button data-dl-act="unlink">🗑 Unlink</button>
        </div>
      `;
      list.appendChild(card);
      card.querySelectorAll<HTMLButtonElement>('[data-dl-act]').forEach((b) => {
        b.addEventListener('click', (e) => {
          e.stopPropagation();
          const act = b.dataset.dlAct as 'play' | 'remix' | 'unlink' | 'pull';
          if (act === 'play') this.hide();
          this.onDownloadedAction?.(act, rid);
        });
      });
      this.bindCompletionsClick(card, lvl.meta.name);
    }
  }

  private renderVerifierBadge(
    verifier: { userId: string; displayName: string; firstCompletedAt: number } | null,
    cachedAt: number | null,
    remoteId: string | null,
  ): string {
    if (verifier) {
      const cachedHint = cachedAt
        ? `<span style="color:#444;font-size:9px"> (as of ${this.relativeTime(cachedAt)})</span>`
        : '';
      const clickable = remoteId && this.syncManager;
      const cursorAttr = clickable ? ';cursor:pointer' : '';
      const dataAttr = clickable ? ` data-show-completions="${escape(remoteId)}"` : '';
      const affordance = clickable ? ' <span style="color:#888">👥</span>' : '';
      return `<div style="font-size:10px;color:#00ffff;margin-top:2px${cursorAttr}"${dataAttr}>🏆 verified by ${escape(verifier.displayName)}${affordance}${cachedHint}</div>`;
    }
    return `<div style="font-size:10px;color:#cc8a3a;margin-top:2px">⚠ Not yet verified</div>`;
  }

  private openCompletionsModal(remoteId: string, levelName: string): void {
    if (!this.syncManager) return;
    const mgr = this.syncManager;
    this.completionsModal.show(levelName, remoteId, async (cursor) => {
      return mgr.listCompletions(remoteId, { cursor, limit: 20 });
    });
  }

  private bindCompletionsClick(card: HTMLElement, levelName: string): void {
    card.querySelector('[data-show-completions]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      const target = e.currentTarget as HTMLElement;
      const rid = target.dataset.showCompletions;
      if (rid) this.openCompletionsModal(rid, levelName);
    });
  }

  private relativeTime(ms: number): string {
    const diff = Date.now() - ms;
    const s = Math.floor(diff / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  }

  private renderDownloadedChip(state: string): string {
    const chipStyle = 'padding:2px 8px;border-radius:10px;font-size:0.6rem;white-space:nowrap';
    switch (state) {
      case 'update_available':
        return `<span class="sync-chip" style="${chipStyle};background:#8855ff22;color:#8855ff">↓ UPDATE</span>`;
      case 'removed_from_server':
        return `<span class="sync-chip" style="${chipStyle};background:#333;color:#888">REMOVED</span>`;
      case 'synced':
      default:
        return `<span class="sync-chip" style="${chipStyle};background:#00440033;color:#66ff99">⬇ DOWNLOADED</span>`;
    }
  }

  private renderUsers(list: HTMLElement, userLevels: LevelData[]): void {
    list.innerHTML = '';
    const addNew = document.createElement('div');
    addNew.className = 'ls-card ls-new';
    addNew.textContent = '+ New Level';
    addNew.addEventListener('click', (e) => { e.stopPropagation(); this.onNew?.(); });
    list.appendChild(addNew);

    const signedInEarly = !!this.storage?.getSession();
    if (userLevels.length === 0 && !(signedInEarly && this.remoteOnly.length > 0)) {
      const empty = document.createElement('div');
      empty.style.cssText = 'color:#667;text-align:center;padding:12px';
      empty.textContent = 'No levels yet. Tap "+ New" above, or clone a built-in.';
      list.appendChild(empty);
      return;
    }

    for (const lvl of userLevels) {
      const card = document.createElement('div');
      card.className = 'ls-card';
      const signedIn = !!this.storage?.getSession();
      const status = signedIn ? this.syncMap.get(lvl.meta.id) : undefined;
      const chipHtml = signedIn ? this.renderChip(lvl, status) : '';
      const publishBtn = signedIn && status?.state === 'synced'
        ? `<button data-act="${status.isPublic ? 'unpublish' : 'publish'}">${status.isPublic ? '🔒 Unpublish' : '🌐 Publish'}</button>`
        : '';
      const verifierBadge = lvl.meta.remoteId
        ? this.renderVerifierBadge(status?.verifier ?? null, null, lvl.meta.remoteId)
        : '';
      card.innerHTML = `
        <div class="row">
          <div>
            <div style="color:#00ffff;font-weight:bold">${escape(lvl.meta.name)}</div>
            <div style="font-size:10px;color:#678">${escape(lvl.meta.difficulty)} · ${escape(lvl.meta.author ?? '')}</div>
            ${verifierBadge}
          </div>
          <div style="display:flex;align-items:center;gap:6px">${chipHtml}</div>
        </div>
        <div class="row actions" style="margin-top:6px;justify-content:flex-end">
          <button data-act="play">▶ Play</button>
          <button data-act="edit">🛠 Edit</button>
          <button data-act="clone">📋 Clone</button>
          ${publishBtn}
          <button data-act="delete">🗑</button>
        </div>
      `;
      if (status) {
        const tint = ({
          'edited': '#ff8800', 'conflict': '#ff4444', 'update_available': '#8855ff',
          'removed_from_server': '#888',
        } as Record<string, string>)[status.state] ?? '#00ffff44';
        card.style.borderColor = tint;
      }
      list.appendChild(card);
      card.querySelector('[data-act="play"]')!.addEventListener('click', (e) => { e.stopPropagation(); this.hide(); this.onSelect?.(lvl.meta.id, 'user'); });
      card.querySelector('[data-act="edit"]')!.addEventListener('click', (e) => { e.stopPropagation(); this.hide(); this.onEdit?.(lvl.meta.id); });
      card.querySelector('[data-act="clone"]')!.addEventListener('click', (e) => { e.stopPropagation(); this.onClone?.(lvl.meta.id, 'user'); });
      card.querySelector('[data-act="delete"]')!.addEventListener('click', (e) => { e.stopPropagation(); this.onDelete?.(lvl.meta.id); });
      card.querySelectorAll<HTMLButtonElement>('[data-sync-act]').forEach((b) => {
        b.addEventListener('click', (e) => {
          e.stopPropagation();
          const act = b.dataset.syncAct as 'push' | 'pull' | 'resolve' | 'clear-link';
          this.onSyncAction?.(act, lvl.meta.id);
        });
      });
      card.querySelector('[data-act="publish"]')?.addEventListener('click', (e) => { e.stopPropagation(); this.onSyncAction?.('publish', lvl.meta.id); });
      card.querySelector('[data-act="unpublish"]')?.addEventListener('click', (e) => { e.stopPropagation(); this.onSyncAction?.('unpublish', lvl.meta.id); });
      this.bindCompletionsClick(card, lvl.meta.name);
    }

    // Render remote-only levels (owned by this account but not yet on this device)
    const signedIn = !!this.storage?.getSession();
    if (signedIn && this.remoteOnly.length > 0) {
      const sep = document.createElement('div');
      sep.style.cssText = 'color:#667;text-align:center;font-size:11px;padding:10px 0 4px;letter-spacing:0.1em';
      sep.textContent = `— ON YOUR ACCOUNT (${this.remoteOnly.length}) —`;
      list.appendChild(sep);
      for (const r of this.remoteOnly) {
        const card = document.createElement('div');
        card.className = 'ls-card';
        card.style.borderColor = '#8855ff88';
        card.innerHTML = `
          <div class="row">
            <div>
              <div style="color:#b99aff;font-weight:bold">${escape(r.name)}</div>
              <div style="font-size:10px;color:#678">${escape(r.difficulty)}${r.isPublic ? ' · 🌐 public' : ''}</div>
            </div>
            <div style="display:flex;align-items:center;gap:6px">
              <span class="sync-chip" style="background:#8855ff22;color:#b99aff;padding:2px 8px;border-radius:10px;font-size:0.6rem;white-space:nowrap">REMOTE</span>
              <button data-act="pull-remote" style="background:#8855ff22;border:1px solid #8855ff;color:#b99aff;padding:4px 10px;font:inherit;font-size:0.7rem;border-radius:3px">PULL</button>
            </div>
          </div>
        `;
        list.appendChild(card);
        card.querySelector('[data-act="pull-remote"]')!.addEventListener('click', (e) => {
          e.stopPropagation();
          this.onPullRemoteOnly?.(r.remoteId);
        });
      }
    }
  }

  private renderChip(_lvl: LevelData, status: LevelSyncStatus | undefined): string {
    if (!status) {
      return `<span class="sync-chip" style="background:#333;color:#888;padding:2px 8px;border-radius:10px;font-size:0.6rem;white-space:nowrap">CHECKING…</span>`;
    }
    switch (status.state) {
      case 'local_only':
        return `<span class="sync-chip" style="background:#333;color:#888;padding:2px 8px;border-radius:10px;font-size:0.6rem;white-space:nowrap">LOCAL</span>
                <button data-sync-act="push" style="background:#00ffff22;border:1px solid #00ffff;color:#00ffff;padding:4px 10px;font:inherit;font-size:0.7rem;border-radius:3px">PUSH</button>`;
      case 'synced':
        return `<span class="sync-chip" style="background:#00440033;color:#00cc66;padding:2px 8px;border-radius:10px;font-size:0.6rem;white-space:nowrap">SYNCED</span>`;
      case 'edited':
        return `<span class="sync-chip" style="background:#ff880022;color:#ff8800;padding:2px 8px;border-radius:10px;font-size:0.6rem;white-space:nowrap">EDITED</span>
                <button data-sync-act="push" style="background:#ff880022;border:1px solid #ff8800;color:#ff8800;padding:4px 10px;font:inherit;font-size:0.7rem;border-radius:3px">PUSH</button>`;
      case 'update_available':
        return `<span class="sync-chip" style="background:#8855ff22;color:#8855ff;padding:2px 8px;border-radius:10px;font-size:0.6rem;white-space:nowrap">UPDATE</span>
                <button data-sync-act="pull" style="background:#8855ff22;border:1px solid #8855ff;color:#8855ff;padding:4px 10px;font:inherit;font-size:0.7rem;border-radius:3px">PULL</button>`;
      case 'conflict':
        return `<span class="sync-chip" style="background:#ff444422;color:#ff4444;padding:2px 8px;border-radius:10px;font-size:0.6rem;white-space:nowrap">CONFLICT</span>
                <button data-sync-act="resolve" style="background:#ff444422;border:1px solid #ff4444;color:#ff4444;padding:4px 10px;font:inherit;font-size:0.7rem;border-radius:3px">RESOLVE</button>`;
      case 'removed_from_server':
        return `<span class="sync-chip" style="background:#333;color:#888;padding:2px 8px;border-radius:10px;font-size:0.6rem;white-space:nowrap">REMOVED</span>
                <button data-sync-act="clear-link" style="background:#111;border:1px solid #333;color:#888;padding:4px 10px;font:inherit;font-size:0.7rem;border-radius:3px">CLEAR</button>`;
    }
  }

  hide(): void { if (this.element) { this.element.remove(); this.element = null; } }
}

function escape(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]!));
}
